import {
  Box,
  Flex,
  PrimaryButton,
  Stack,
  TertiaryButton,
  Text,
} from "flicket-ui";
import { useRouter } from "next/router";
import { getImageSrc, withPropagation } from "~lib";
import { EventKind } from "~graphql/sdk";
import { useOrganization, useTextColour } from "~hooks";
import useCaptureClick from "~telemetry/traces/useCaptureClick";
import {
  AttributeNames,
  InteractionNames,
} from "~telemetry/enums/AttributeNames";
import { FormatDateOptions } from "@flicket/utils";
import { formatEventDateRange } from "~lib/helpers/formatDate";
import { EventsList } from "~features/events/hooks/useEventsService";
import { DateDisplay, formatDateForDisplay } from "./events.DateDisplay";
import { FixedRatioImage } from "~components/common";
import { getEventVenueLocation } from "~lib/helpers/getEventVenueLocation";

const medium = "sm";

type CardProps = Pick<
  EventsList[number],
  | "title"
  | "id"
  | "venue"
  | "startDate"
  | "endDate"
  | "eventKind"
  | "hasPublicRelease"
  | "thumbnail"
  | "logo"
  | "descriptionImage"
>;

export const EventCard = ({
  id,
  title,
  venue,
  hasPublicRelease,
  startDate,
  endDate,
  eventKind,
  thumbnail,
  logo,
  descriptionImage,
}: CardProps) => {
  const router = useRouter();
  const isDigitalEvent = eventKind === EventKind.Digital;
  const { organization, hasFeature } = useOrganization();

  const navigateEvent = async () =>
    router.push("/events/[eventId]", `/events/${id}`);
  const navigateReservation = async () => {
    return router.push(
      "/events/[eventId]/reservation",
      `/events/${id}/reservation`
    );
  };

  const captureClick = useCaptureClick({
    forwardToNest: true,
    eventId: id || "",
    pageName: "event-list-page",
  });

  const primaryButtonLabel = isDigitalEvent ? "Get access" : "Buy tickets";

  const dateOptions: FormatDateOptions = {
    showStartTime: !hasFeature("hideEventTime"),
    timeZone: venue?.timezone,
    locale: venue?.locale,
  };

  const imageSrc = getImageSrc(logo, "");

  const { day, month } = formatDateForDisplay(startDate, dateOptions);

  const dateString = formatEventDateRange(startDate, endDate, {
    format: "basicShort",
    ...dateOptions,
  });

  const locationString = getEventVenueLocation(venue);

  // Check if certain organizations should show the buttons (like in the original code)
  const shouldShowButtons =
    organization.id !== "58106ce0-e1cd-47cb-9c59-0d0192ca6df8" &&
    organization.id !== "45339f71-c0bb-41a9-b642-7d30f1321a84";

  const textColor = useTextColour();

  return (
    <Stack
      gap={3}
      py={2}
      flex="1"
      direction={{
        _: "vertical",
        [medium]: "horizontal",
      }}
      minHeight="180px"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={async () => navigateEvent()}
      borderTop="1px solid"
      borderTopColor="N200"
      cursor="pointer"
    >
      {!!imageSrc && (
        <Box>
          <FixedRatioImage
            alt={title}
            aspectRatio="5 / 2"
            src={imageSrc}
            metaData={logo?.metaData}
            width={{
              _: "100%",
              [medium]: "360px",
              ["md"]: "430px",
            }}
            overflow="hidden"
            borderRadius={"md"}
            objectFit="contain"
            useNearFitCover={true}
          />
        </Box>
      )}

      <Stack
        direction={{
          _: "horizontal",
          [medium]: "horizontal",
        }}
        alignItems={{ md: "center" }}
        flex="1"
        flexWrap={"nowrap"}
        gap={3}
      >
        <Stack
          justifyContent={{
            _: "space-between",
            [medium]: "flex-start",
          }}
          direction={{
            _: "vertical",
          }}
          alignItems={{
            _: "stretch",
            [medium]: "flex-start",
          }}
          flex={1}
          gap={2}
          // bg="green"
        >
          <Stack
            direction={"horizontal"}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
            // bg="red"
          >
            <Stack
              direction={"vertical"}
              alignItems={"flex-start"}
              maxW={{ _: "", [medium]: "460px" }}
            >
              <Text as="h1" variant="header.S" fontWeight="bold" mb={1}>
                {title}
                {isDigitalEvent ? " (Online Event)" : ""}
              </Text>
              <Text variant="regular">{dateString}</Text>
              <Text variant="regular">{locationString}</Text>
            </Stack>
            <Flex
              flexShrink={0}
              justifyContent={"flex-end"}
              textAlign={"center"}
              display={{ [medium]: "none" }}
              pl={5}
            >
              <DateDisplay day={day} month={month} />
            </Flex>
          </Stack>
          {hasPublicRelease && shouldShowButtons && (
            <Stack
              justifyContent="flex-end"
              alignItems="flex-start"
              direction="horizontal"
              gap={2}
              flex={1}
            >
              <TertiaryButton
                alignSelf={{ _: "center", [medium]: "flex-start" }}
                flex={{
                  _: "1",
                  [medium]: "unset",
                }}
                onClick={(e) => {
                  navigateEvent &&
                    withPropagation(e, () => void navigateEvent());
                }}
              >
                Event info
              </TertiaryButton>

              <PrimaryButton
                alignSelf={{
                  _: "stretch",
                  [medium]: "flex-start",
                }}
                color={textColor}
                flex={{
                  _: "1",
                  [medium]: "unset",
                }}
                onClick={captureClick(
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  (e) => withPropagation(e, navigateReservation),
                  InteractionNames.BUTTON_PRIMARY,
                  {
                    [AttributeNames.BUTTON_LABEL]: primaryButtonLabel,
                  }
                )}
              >
                {primaryButtonLabel}
              </PrimaryButton>
            </Stack>
          )}
        </Stack>
        <Flex
          flexShrink={0}
          justifyContent={"flex-end"}
          textAlign={"center"}
          display={{
            _: "none !important",
            [medium]: "flex !important",
          }}
          pl={1}
        >
          <DateDisplay day={day} month={month} />
        </Flex>
      </Stack>
    </Stack>
  );
};
